import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useEditor } from '../state/store';
import type { SectionSchema, SettingSchema } from '../api/types';
import { isVisible } from '../lib/visible-if';
import {
  TextField,
  TextareaField,
  RichTextField,
  NumberField,
  RangeField,
  CheckboxField,
  SelectField,
  RadioField,
  ColorField,
  ImageField,
  UrlField,
  LinkListField,
  ProductField,
  CollectionField,
  PageField,
  type FieldProps,
} from './fields/Fields';

/**
 * Right panel — Polaris card style.
 * White surface, soft subtle dividers, collapsible block cards.
 */

function renderField(props: FieldProps) {
  const type = (props.schema.type || '').toLowerCase();
  switch (type) {
    case 'text': return <TextField {...props} />;
    case 'textarea': return <TextareaField {...props} />;
    case 'richtext':
    case 'html': return <RichTextField {...props} />;
    case 'number': return <NumberField {...props} />;
    case 'range': return <RangeField {...props} />;
    case 'checkbox':
    case 'bool':
    case 'boolean': return <CheckboxField {...props} />;
    case 'select': return <SelectField {...props} />;
    case 'radio': return <RadioField {...props} />;
    case 'color': return <ColorField {...props} />;
    case 'image':
    case 'image_picker':
    case 'file':
    case 'video':
    case 'video_picker': return <ImageField {...props} />;
    case 'url':
    case 'link': return <UrlField {...props} />;
    case 'link_list':
    case 'menu': return <LinkListField {...props} />;
    case 'product':
    case 'product_picker': return <ProductField {...props} />;
    case 'collection':
    case 'collection_picker': return <CollectionField {...props} />;
    case 'page': return <PageField {...props} />;
    case 'header':
      // Estilo Shopify-like: borda forte de separação acima, mais
      // padding em volta do título. Antes a borda era `border-subdued`
      // (quase invisível) e o título colado no campo anterior — ficava
      // difícil notar onde um grupo começa e outro termina.
      //
      // Shopify spec: o texto vem em `content` (não `label`).
      return (
        <div className="-mx-4 mb-3 mt-5 border-t border-border bg-surface-subdued px-4 py-3 first:mt-0 first:border-t-0 first:bg-transparent first:py-0 first:pt-0">
          <div className="text-[12px] font-semibold uppercase tracking-wide text-text">
            {props.schema.content || props.schema.label || ''}
          </div>
          {props.schema.info && (
            <div className="mt-1 text-[11px] leading-relaxed text-text-subdued">{props.schema.info}</div>
          )}
        </div>
      );
    case 'paragraph':
      return (
        <p className="mb-3 text-[12px] leading-relaxed text-text-subdued">
          {props.schema.content || props.schema.label || props.schema.info || ''}
        </p>
      );
    case 'divider':
      /*
        Linha horizontal pra separar grupos de settings. Usado quando
        um `header` não faz sentido (sem título de grupo) mas as
        configs precisam de separação visual — tipicamente entre uma
        config e um `paragraph` informativo logo abaixo, pra que o
        usuário não confunda o paragraph com a descrição da config
        anterior.

        No JSON: `{ "type": "divider" }` — sem id, label ou content.
      */
      return (
        <hr className="-mx-4 my-4 border-t border-border" />
      );
    default:
      return <TextField {...props} />;
  }
}

/**
 * Quando o usuário clica numa section ou bloco no preview e o
 * inspector troca de alvo, a primeira dropdown (<select>) do painel
 * é auto-focada e aberta via `showPicker()` (Chrome 111+, Safari
 * 17.3+). Isso evita a fricção de "clicar 2x" — na ativação do
 * Menu/Produto/Coleção picker, o dropdown já tá aberto pra escolher.
 *
 * Keyed por `key` (normalmente sectionId ou sectionId:blockId), então
 * dispara de novo quando a seleção muda.
 */
function useAutoOpenFirstSelect(key: string | undefined) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!key || !ref.current) return;
    // Pequeno delay pra dar tempo do render terminar e os selects
    // estarem no DOM com as options já populadas.
    const id = window.setTimeout(() => {
      const firstSelect = ref.current?.querySelector<HTMLSelectElement>(
        'select:not([disabled])'
      );
      if (!firstSelect) return;
      try {
        firstSelect.focus({ preventScroll: true });
        // showPicker é o jeito moderno — abre o dropdown nativo sem
        // exigir clique do usuário. Falha silenciosa em browsers
        // antigos ou quando o call não é considerado "user-activated"
        // (nesse caso, o focus já chama atenção visual).
        if (typeof (firstSelect as any).showPicker === 'function') {
          (firstSelect as any).showPicker();
        }
      } catch {
        /* showPicker pode lançar em alguns contextos — ignora. */
      }
    }, 80);
    return () => window.clearTimeout(id);
  }, [key]);
  return ref;
}

/**
 * Preserva o scroll do body do Inspector entre re-renders/updates.
 *
 * O usuário reportou: "quando altero algo que faz request na config
 * tá dando scroll la pra cima, o bom seria manter o usuário onde ele
 * estava editando". Acontece porque certas mudanças (ex.: selection
 * muda entre section/block, um loading flag é togglado brevemente,
 * re-mount do componente por troca de chave) fazem a div scrollable
 * recriar no DOM e perder o scrollTop.
 *
 * A gente guarda a posição num `Map<key, scrollTop>` em módulo
 * (sobrevive re-mounts) e restaura via `useLayoutEffect` — que roda
 * ANTES do browser pintar, então o usuário nunca vê o salto pra 0.
 *
 * Key = mesmo identificador usado pro auto-open (sectionId ou
 * sectionId:blockId). Navegação pra outra section começa do topo.
 */
const inspectorScrollMap = new Map<string, number>();

function useInspectorScrollRestore(
  key: string | undefined,
  existingRef: React.RefObject<HTMLDivElement>
) {
  // Restaura antes do paint.
  useLayoutEffect(() => {
    if (!key || !existingRef.current) return;
    const saved = inspectorScrollMap.get(key);
    if (typeof saved === 'number') {
      existingRef.current.scrollTop = saved;
    }
  }, [key, existingRef]);

  // Salva o scroll continuamente enquanto o usuário rola.
  useEffect(() => {
    if (!key || !existingRef.current) return;
    const el = existingRef.current;
    const onScroll = () => {
      inspectorScrollMap.set(key, el.scrollTop);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    // Salva no unmount também (captura a última posição antes de
    // trocar de section).
    return () => {
      inspectorScrollMap.set(key, el.scrollTop);
      el.removeEventListener('scroll', onScroll);
    };
  }, [key, existingRef]);
}

function Fields({
  schema,
  values,
  onChange,
}: {
  schema: SettingSchema[];
  values: Record<string, any>;
  onChange: (id: string, v: any) => void;
}) {
  // Defensive: schemas coming from the engine are user-authored JSON
  // inside `{% schema %}` blocks. A malformed schema (e.g. `"settings": {...}`
  // instead of `"settings": [...]`) used to crash the whole inspector
  // with `e.map is not a function`. Coerce to an array so the UI stays
  // alive; the offending block just has no editable fields.
  const items = Array.isArray(schema) ? schema : [];

  /*
    Mapa "efetivo" pra avaliar `visible_if`: pega os defaults do schema
    pra IDs que ainda não foram salvos no `values`. Sem isso, settings
    com `default: true` apareciam como `undefined` na hora de avaliar
    e qualquer dependente (`visible_if: 'show_installments'`) ficava
    invisível mesmo quando o toggle visualmente estava marcado.

    `values` (saved) tem prioridade — se o lojista desligou o toggle
    e SALVOU `false`, é isso que vale, não o default `true`.
  */
  const effectiveValues = (() => {
    const merged: Record<string, any> = {};
    for (const s of items) {
      if (s.id && s.default !== undefined && !(s.id in values)) {
        merged[s.id] = s.default;
      }
    }
    return { ...merged, ...values };
  })();

  /*
    Render flat — sem wrapper div — pra que o seletor CSS
    `first:border-t-0:first-child` no header funcione corretamente.
    Antes cada header era envolto em `<div key="h-X">`, então o
    elemento do header era o ÚNICO filho do seu wrapper e SEMPRE
    casava `:first-child`. Resultado: todos os headers ficavam sem
    borda de separação (visualmente colados no campo anterior).

    Com `React.Fragment`, headers e fields viram irmãos diretos do
    container externo — só o primeiro elemento da lista casa
    `:first-child`, e a borda separa grupos como esperado.
  */
  return (
    <>
      {items.map((s, i) => {
        // Conditional visibility: both section-level settings and block-level
        // settings resolve against the same `values` map being rendered, so a
        // single check covers both cases.
        if (!isVisible(s.visible_if, effectiveValues)) return null;

        if (s.type === 'header' || s.type === 'paragraph' || s.type === 'divider') {
          return (
            <Fragment key={`h-${i}`}>
              {renderField({ schema: s, value: null, onChange: () => {} } as any)}
            </Fragment>
          );
        }
        if (!s.id) return null;

        /*
          AUTO-COVER pra uploads de MP4 — quando o user sobe um vídeo
          num setting tipo `file`/`image_picker`, o engine extrai o
          primeiro frame e devolve no `uploaded.cover.name`. O
          `ImageField` chama `onAutoCover(coverName)` quando isso
          acontece. Aqui no Fields, a gente busca um setting IRMÃO
          chamado `cover_image` na mesma section/block, e se achar,
          aplica o coverName lá automaticamente.

          Convenção: pra disparar auto-cover, o block precisa ter
          AMBOS settings `video` (file/image_picker) E `cover_image`
          (image_picker). É o padrão do social-media reels.

          Sem isso, seller subia vídeo e tinha que subir capa
          manualmente; agora vem populado automaticamente do
          primeiro frame extraído pelo engine.
        */
        const hasSiblingCover =
          items.some((sib) => sib.id === 'cover_image') &&
          (s.type === 'file' || s.type === 'image_picker' || s.type === 'image');
        const onAutoCover = hasSiblingCover
          ? (coverName: string) => onChange('cover_image', coverName)
          : undefined;

        return (
          <Fragment key={s.id}>
            {renderField({
              schema: s,
              value: values[s.id] ?? s.default,
              onChange: (v: any) => onChange(s.id!, v),
              onAutoCover,
            } as any)}
          </Fragment>
        );
      })}
    </>
  );
}

// ---------------------------------------------------------------------------

function InspectorHeader({
  eyebrow, title, sublabel, onBack,
}: { eyebrow?: string; title: string; sublabel?: string; onBack?(): void }) {
  return (
    <header className="flex items-center gap-2 border-b border-border px-4 py-3">
      {onBack && (
        <button
          onClick={onBack}
          className="lc-btn-ghost flex h-7 w-7 items-center justify-center"
          title="Voltar"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="m15 18-6-6 6-6" />
          </svg>
        </button>
      )}
      <div className="min-w-0">
        {eyebrow && (
          <div className="text-[11px] font-medium uppercase tracking-wider text-text-subdued">
            {eyebrow}
          </div>
        )}
        <div className="truncate text-[14px] font-semibold text-text">{title}</div>
        {sublabel && (
          <div className="mt-0.5 truncate text-[11px] text-text-subdued">{sublabel}</div>
        )}
      </div>
    </header>
  );
}

// ---------------------------------------------------------------------------

function SectionInspector() {
  const selection = useEditor((s) => s.selection);
  const getSec = useEditor((s) => {
    if (!selection || selection.kind !== 'section') return null;
    if (selection.group === 'template') return s.sections[selection.sectionId] || null;
    if (selection.group === 'header-group') return s.headerGroup?.sections[selection.sectionId] || null;
    if (selection.group === 'cart-drawer-group') return s.cartDrawerGroup?.sections[selection.sectionId] || null;
    return s.footerGroup?.sections[selection.sectionId] || null;
  });
  const schemas = useEditor((s) => s.metadata?.theme.schemas || {});
  const updateSectionSetting = useEditor((s) => s.updateSectionSetting);
  const select = useEditor((s) => s.select);
  const sectionKey = selection?.kind === 'section' ? selection.sectionId : undefined;
  const bodyRef = useAutoOpenFirstSelect(sectionKey);
  useInspectorScrollRestore(sectionKey, bodyRef);

  if (!selection || selection.kind !== 'section') return null;
  if (!getSec) return <EmptyInspector label="Seção não encontrada" />;

  const section = getSec;
  const schema: SectionSchema = schemas[section.type] || {};
  const settings = schema.settings || [];

  return (
    <div className="flex h-full flex-col">
      <InspectorHeader
        eyebrow="Seção"
        title={schema.name || section.type}
        sublabel={section.type}
        onBack={() => select(null)}
      />
      <div ref={bodyRef} className="lc-scroll flex-1 overflow-y-auto px-4 py-4">
        {settings.length === 0 ? (
          <p className="text-[12px] text-text-subdued">Esta seção não possui configurações.</p>
        ) : (
          <Fields
            schema={settings}
            values={section.settings}
            onChange={(id, v) =>
              updateSectionSetting({ group: selection.group, sectionId: selection.sectionId }, id, v)
            }
          />
        )}

        {section.blockOrder.length > 0 && (
          <div className="mt-6 border-t border-border-subdued pt-3">
            <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-text-subdued">
              Blocos ({section.blockOrder.length})
            </div>
            <p className="text-[12px] text-text-subdued">
              Selecione um bloco na barra lateral para editar.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function BlockInspector() {
  const selection = useEditor((s) => s.selection);
  const getSection = useEditor((s) => {
    if (!selection || selection.kind !== 'block') return null;
    if (selection.group === 'template') return s.sections[selection.sectionId] || null;
    if (selection.group === 'header-group') return s.headerGroup?.sections[selection.sectionId] || null;
    if (selection.group === 'cart-drawer-group') return s.cartDrawerGroup?.sections[selection.sectionId] || null;
    return s.footerGroup?.sections[selection.sectionId] || null;
  });
  const schemas = useEditor((s) => s.metadata?.theme.schemas || {});
  const updateBlockSetting = useEditor((s) => s.updateBlockSetting);
  const removeBlock = useEditor((s) => s.removeBlock);
  const select = useEditor((s) => s.select);
  const blockKey = selection?.kind === 'block'
    ? `${selection.sectionId}:${selection.blockId}`
    : undefined;
  const bodyRef = useAutoOpenFirstSelect(blockKey);
  useInspectorScrollRestore(blockKey, bodyRef);

  if (!selection || selection.kind !== 'block') return null;
  if (!getSection) return <EmptyInspector label="Bloco não encontrado" />;

  const section = getSection;
  const block = section.blocks[selection.blockId];
  if (!block) return <EmptyInspector label="Bloco não encontrado" />;

  const schema: SectionSchema = schemas[section.type] || {};
  const blockSchema = (schema.blocks || []).find((b) => b.type === block.type);
  const blockSettings = blockSchema?.settings || [];
  const sectionLabel = schema.name || section.type;
  const blockLabel = blockSchema?.name || block.type;

  return (
    <div className="flex h-full flex-col">
      <header className="border-b border-border px-4 py-3">
        <div className="flex items-center gap-2">
          <button
            onClick={() => select({ kind: 'section', group: selection.group, sectionId: selection.sectionId })}
            className="lc-btn-ghost flex h-7 w-7 items-center justify-center"
            title="Voltar para a seção"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="m15 18-6-6 6-6" />
            </svg>
          </button>
          <div className="min-w-0 flex-1">
            <button
              onClick={() => select({ kind: 'section', group: selection.group, sectionId: selection.sectionId })}
              className="truncate text-[11px] text-text-subdued hover:text-text"
            >
              {sectionLabel} /
            </button>
            <div className="mt-0.5 truncate text-[14px] font-semibold text-text">
              {blockLabel}
            </div>
          </div>
          <button
            onClick={() => removeBlock({ group: selection.group, sectionId: selection.sectionId, blockId: selection.blockId })}
            className="flex h-7 w-7 items-center justify-center rounded-md text-icon hover:bg-critical/10 hover:text-critical"
            title="Excluir bloco"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M6 6l1 14a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-14" />
            </svg>
          </button>
        </div>
      </header>

      <div ref={bodyRef} className="lc-scroll flex-1 overflow-y-auto px-4 py-4">
        {blockSettings.length === 0 ? (
          <p className="text-[12px] text-text-subdued">Este bloco não possui configurações.</p>
        ) : (
          // Merge section.settings INTO block values for visible_if evaluation.
          // Block-level expressions can write `section.settings.display_mode == 'stories'`
          // and it resolves against the parent section. Block keys override section keys
          // on collision (block is the inner scope). The on-change still writes to the
          // block only — section.settings is read-only context for visibility checks.
          <Fields
            schema={blockSettings}
            values={{ ...(section.settings || {}), ...(block.settings || {}) }}
            onChange={(id, v) =>
              updateBlockSetting(
                {
                  group: selection.group,
                  sectionId: selection.sectionId,
                  blockId: selection.blockId,
                },
                id,
                v
              )
            }
          />
        )}
      </div>
    </div>
  );
}

function GlobalInspector() {
  const globalSchema = useEditor((s) => s.metadata?.theme.globalSchema || []);
  const globalSettings = useEditor((s) => s.globalSettings);
  const update = useEditor((s) => s.updateGlobalSetting);
  const selection = useEditor((s) => s.selection);
  const [query, setQuery] = useState('');
  const [openIdx, setOpenIdx] = useState<number | null>(0);

  //
  // groupFilter: quando o `select({ kind: 'global', groupFilter: 'X' })`
  // é chamado (ex: de um contexto onde queremos focar só uma aba como
  // "Carrinho gaveta"), filtramos os grupos pra mostrar SÓ a que tem
  // o nome correspondente. Isso evita o lojista ficar perdido entre
  // 15 abas globais quando ele pediu pra editar UM CONTEXTO específico.
  //
  const groupFilter = selection?.kind === 'global' ? selection.groupFilter : undefined;

  // Each entry in globalSchema is a group like { name: 'Cores', settings: […] }.
  // Flatten each group, filter by the search query, and render as a collapsible
  // <details> so the user can browse tens of settings without scrolling forever.
  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = groupFilter
      ? (globalSchema || []).filter((g) => g.name === groupFilter)
      : (globalSchema || []);
    return base.map((g) => {
      const fields = (g.settings || []).filter((s) => {
        if (!q) return true;
        const hay = [s.label, s.id, s.info].filter(Boolean).join(' ').toLowerCase();
        return hay.includes(q);
      });
      return { name: g.name, fields };
    });
  }, [globalSchema, query, groupFilter]);

  const totalMatches = groups.reduce((n, g) => n + g.fields.length, 0);

  return (
    <div className="flex h-full flex-col">
      <InspectorHeader
        eyebrow="Tema"
        title={groupFilter || 'Configurações globais'}
      />
      <div className="border-b border-border px-4 py-3">
        <div className="relative">
          <svg
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-icon-subdued"
            width="14" height="14" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.3-4.3" />
          </svg>
          <input
            className="lc-input pl-9"
            placeholder="Buscar configuração…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        {query && (
          <p className="mt-2 text-[11px] text-text-subdued">
            {totalMatches} resultado{totalMatches === 1 ? '' : 's'}
          </p>
        )}
      </div>

      <div className="lc-scroll flex-1 overflow-y-auto px-3 py-3">
        {groups.length === 0 && (
          <p className="px-2 text-[12px] text-text-subdued">
            Nenhuma configuração global definida.
          </p>
        )}
        {groups.map((g, i) => {
          if (g.fields.length === 0) return null;
          const open = query ? true : openIdx === i;
          return (
            <div
              key={`${g.name}-${i}`}
              className="mb-1.5 overflow-hidden rounded-md border border-border bg-surface"
            >
              <button
                onClick={() => setOpenIdx(open ? null : i)}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] font-semibold text-text transition-colors hover:bg-surface-hovered"
              >
                <svg
                  width="12" height="12" viewBox="0 0 24 24"
                  fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                  className={'shrink-0 text-icon-subdued transition-transform ' + (open ? 'rotate-90' : '')}
                >
                  <path d="m9 18 6-6-6-6" />
                </svg>
                <span className="flex-1 truncate">{g.name}</span>
                <span className="text-[11px] font-normal text-text-subdued">
                  {g.fields.length}
                </span>
              </button>
              {open && (
                <div className="border-t border-border-subdued px-3 pb-1 pt-3">
                  <Fields
                    schema={g.fields}
                    values={globalSettings}
                    onChange={(id, v) => update(id, v)}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EmptyInspector({ label }: { label?: string }) {
  return (
    <div className="flex h-full items-center justify-center bg-surface p-6 text-center">
      <div>
        <svg
          width="40" height="40" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
          className="mx-auto mb-3 text-icon-subdued"
        >
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <path d="M9 9h6v6H9z" />
        </svg>
        <p className="text-[13px] text-text">{label || 'Selecione uma seção'}</p>
        <p className="mt-1 text-[12px] text-text-subdued">
          Clique em um item da barra lateral ou no preview.
        </p>
      </div>
    </div>
  );
}

export function Inspector() {
  const selection = useEditor((s) => s.selection);
  return (
    <aside className="flex h-full w-[320px] shrink-0 flex-col border-l border-border bg-surface">
      {!selection && <EmptyInspector />}
      {selection?.kind === 'section' && <SectionInspector />}
      {selection?.kind === 'block' && <BlockInspector />}
      {selection?.kind === 'global' && <GlobalInspector />}
    </aside>
  );
}
